import LoyaltyIcon from '@mui/icons-material/Loyalty';
import {
    Box,
    CircularProgress,
    ClickAwayListener,
    Fade,
    Paper,
    Popper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { toPriceString } from '../../../utils';
import { AgreementPriceClient, PriceAgreementDetailsDto } from '../../PricePrgClient';
import { scrollbar } from '../../theme/scrollbar';

type Props = {
    agreementPriceIds: number[];
    open: boolean;
    priceType: 'in' | 'ext';
    currentAgreementId?: number;
    setOpen(open: boolean): void;
};

const agreementPriceClient = new AgreementPriceClient();

export const AgreementPricesPopperButton = ({
    agreementPriceIds,
    open,
    setOpen,
    priceType,
    currentAgreementId
}: Props) => {
    const [agreementPrices, setAgreementPrices] = useState<PriceAgreementDetailsDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [arrowRef, setArrowRef] = useState<HTMLSpanElement | null>(null);
    const anchorRef = useRef<SVGSVGElement>(null);

    const handleToggle = () => {
        setOpen(!open);
    };

    const handleClose = (event: globalThis.MouseEvent | TouchEvent) => {
        const target = event.target as HTMLElement;
        if (!anchorRef.current?.contains(target)) {
            setOpen(false);
        }
    };

    useEffect(() => {
        if (open) {
            setIsLoading(true);

            agreementPriceClient
                .getByAgreementPriceIds({
                    agreementPriceIds: agreementPriceIds,
                    agreementId: currentAgreementId
                })
                .then(ap => {
                    setAgreementPrices(
                        ap.sort((a, b) => {
                            const nameCompare = a.agreementName
                                .toLocaleLowerCase()
                                .localeCompare(b.agreementName.toLocaleLowerCase());

                            if (nameCompare !== 0) {
                                return nameCompare;
                            }

                            const priceCompare =
                                priceType === 'in'
                                    ? a.inPrice - b.inPrice
                                    : (a.extPrice ?? 0) - (b.extPrice ?? 0);

                            if (priceCompare !== 0) {
                                return priceCompare;
                            }

                            return a.customerName
                                .toLocaleLowerCase()
                                .localeCompare(b.customerName.toLocaleLowerCase());
                        })
                    );
                    setIsLoading(false);
                });
        }
    }, [open, agreementPriceIds, priceType, currentAgreementId]);

    return (
        <Box component="div" sx={{ display: 'flex', padding: '2px' }}>
            <LoyaltyIcon
                aria-haspopup="true"
                onClick={handleToggle}
                opacity={0.5}
                color="primary"
                fontSize="small"
                sx={{ color: 'rgba(0, 39, 255, 1)' }}
                ref={anchorRef}
            />

            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="right"
                sx={theme => ({
                    zIndex: 1300,
                    '&[x-placement*="right"] $arrow': {
                        left: 0,
                        marginLeft: '-0.9em',
                        height: '3em',
                        width: '1em',
                        '&::before': {
                            borderWidth: '1em 1em 1em 0',
                            borderColor: `transparent ${theme.palette.background.paper} transparent transparent`
                        }
                    }
                })}
                modifiers={[
                    {
                        name: 'flip',
                        enabled: true
                    },
                    {
                        name: 'arrow',
                        enabled: true,
                        options: {
                            element: arrowRef
                        }
                    },
                    {
                        name: 'preventOverflow',
                        enabled: true,
                        options: {
                            altBoundry: true
                        }
                    }
                ]}
                transition
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={e => handleClose(e)}>
                        <Fade {...TransitionProps} timeout={350}>
                            <Box component="div">
                                <Box
                                    component="span"
                                    sx={{
                                        position: 'absolute',
                                        fontSize: 7,
                                        width: '3em',
                                        height: '3em',
                                        '&::before': {
                                            content: '""',
                                            margin: 'auto',
                                            display: 'block',
                                            width: 0,
                                            height: 0,
                                            borderStyle: 'solid'
                                        }
                                    }}
                                    ref={setArrowRef}
                                />
                                <Paper
                                    sx={{ width: '550px', padding: '8px', maxHeight: '600px' }}
                                    elevation={4}
                                >
                                    {isLoading ? (
                                        <CircularProgress />
                                    ) : (
                                        <TableContainer
                                            sx={theme => ({
                                                maxHeight: 440,
                                                overflowY: 'auto',
                                                overflowX: 'hidden',
                                                ...scrollbar(theme)
                                            })}
                                        >
                                            <Table size="small" aria-label="Agreement prices" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Agreement</TableCell>
                                                        <TableCell>Customer</TableCell>
                                                        <TableCell align="right">
                                                            {priceType === 'in' ? 'Price' : 'Ext. price'}
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {agreementPrices.map(
                                                        (value: PriceAgreementDetailsDto, index: number) => {
                                                            return (
                                                                <TableRow key={index}>
                                                                    <TableCell component="th" scope="row">
                                                                        {value.agreementName}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {`${value.customerNumber} - ${value.customerName}`}
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        <Box fontWeight="fontWeightBold">
                                                                            {toPriceString(
                                                                                priceType === 'in'
                                                                                    ? value.inPrice
                                                                                    : value.extPrice
                                                                            )}
                                                                        </Box>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        }
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Paper>
                            </Box>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
        </Box>
    );
};
